import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatRole } from './ChatBubble';

/**
 * useChatStream
 *
 * Generic chat-state primitive that absorbs the duplication between
 * the three NutriFlow chat surfaces (NutriKnows, dashboard
 * NutriChatWidget, portal NutriChatTab). Each surface had its own
 * implementation of:
 *
 *   - optimistic user-message append,
 *   - pending assistant-message placeholder,
 *   - in-flight `AbortController` + cleanup on unmount,
 *   - "is the assistant typing right now" boolean.
 *
 * The hook owns those four concerns and stays agnostic of how the
 * actual response arrives. Callers supply a `request` function that
 * receives the latest user message, the abort signal, and a
 * `pushDelta` callback for streaming surfaces. Non-streaming surfaces
 * just `return` the final assistant content; streaming surfaces call
 * `pushDelta` per token and may return an empty string.
 *
 * Non-goals (kept in surface-specific code):
 *   - Persistence / DB writes.
 *   - Source / citation rendering.
 *   - Disclaimer / consent gating.
 *   - Voice transcription, file uploads.
 */

export interface ChatStreamMessage<Extra extends object = object> {
  id: string;
  role: ChatRole;
  content: string;
  pending?: boolean;
  error?: string;
  extra?: Extra;
}

export interface ChatStreamRequestArgs {
  /** The latest user message that triggered this turn. */
  message: string;
  /** Abort signal fired on user-cancel or unmount. */
  signal: AbortSignal;
  /** Append an incremental token to the assistant message. */
  pushDelta: (delta: string) => void;
  /** Attach surface-specific data (e.g. sources) to the assistant message. */
  setExtra: (extra: object) => void;
}

export type ChatStreamRequest = (
  args: ChatStreamRequestArgs,
) => Promise<string | void>;

export interface UseChatStreamOptions<Extra extends object = object> {
  initialMessages?: ChatStreamMessage<Extra>[];
  /** Called when an in-flight request completes successfully. */
  onSettled?: () => void;
}

export function useChatStream<Extra extends object = object>(
  options: UseChatStreamOptions<Extra> = {},
) {
  const [messages, setMessages] = useState<ChatStreamMessage<Extra>[]>(
    options.initialMessages ?? [],
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // Cancel any in-flight request when the component unmounts.
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setError(null);
    setIsStreaming(false);
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const send = useCallback(
    async (text: string, request: ChatStreamRequest) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setError(null);
      setIsStreaming(true);

      const userId = `u-${Date.now()}`;
      const assistantId = `a-${Date.now()}`;
      const userMsg: ChatStreamMessage<Extra> = {
        id: userId,
        role: 'user',
        content: trimmed,
      };
      const assistantMsg: ChatStreamMessage<Extra> = {
        id: assistantId,
        role: 'assistant',
        content: '',
        pending: true,
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      const pushDelta = (delta: string) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: m.content + delta } : m,
          ),
        );
      };

      const setExtra = (extra: object) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, extra: { ...(m.extra ?? {}), ...extra } as Extra }
              : m,
          ),
        );
      };

      try {
        const final = await request({
          message: trimmed,
          signal: ctrl.signal,
          pushDelta,
          setExtra,
        });

        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== assistantId) return m;
            // For non-streaming requests, the caller returns the final
            // string instead of using pushDelta — accept that here.
            const content =
              typeof final === 'string' && m.content.length === 0
                ? final
                : m.content;
            return { ...m, content, pending: false };
          }),
        );
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          // User cancelled.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, pending: false } : m,
            ),
          );
        } else {
          const msg = (err as Error).message ?? 'Unbekannter Fehler.';
          setError(msg);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, pending: false, error: msg }
                : m,
            ),
          );
        }
      } finally {
        if (abortRef.current === ctrl) abortRef.current = null;
        setIsStreaming(false);
        options.onSettled?.();
      }
    },
    [isStreaming, options],
  );

  return {
    messages,
    setMessages,
    isStreaming,
    error,
    send,
    stop,
    reset,
  };
}
